/**
 * The in-memory release capability tuple (PC-B rev 9, section 15
 * decision 5). Created by the BUNDLED DOCUMENT JAVASCRIPT — never by
 * static HTML, the service worker, or a network response — so reading it
 * proves which JavaScript is actually RUNNING in a context. `skipWaiting`
 * and `clients.claim` do not replace running JavaScript, so a service
 * worker or cache version is never document proof; this tuple is.
 *
 * The R0-R6 rollout compares all three fields against the release
 * manifest in EVERY enumerated context (device, browser profile, tab,
 * document, installed PWA) before the R5/R6 transitions. Settings About
 * displays the SAME in-memory tuple (never a re-derived copy).
 *
 * `buildId` is the exact reviewed 40-character Git commit staged via the
 * `VITE_BUILD_ID` Vercel env var. The immutable Vercel `dpl_...` deployment
 * identity is separate operator evidence. A build without `buildId` reports
 * `dev-unset`, which the runbook
 * treats as NOT marker-current.
 */

import {
  RELEASE_CONTAINMENT,
  RELEASE_KEEPSAKE_ENVELOPE,
} from '@/lib/keepsake/constants';

export interface AdiaReleaseCapabilities {
  buildId: string;
  containment: typeof RELEASE_CONTAINMENT;
  keepsakeEnvelope: typeof RELEASE_KEEPSAKE_ENVELOPE;
}

declare global {
  interface Window {
    __ADIA_RELEASE_CAPABILITIES__?: AdiaReleaseCapabilities;
  }
}

function buildTuple(): AdiaReleaseCapabilities {
  const raw = import.meta.env.VITE_BUILD_ID;
  const buildId = typeof raw === 'string' && /^[0-9a-f]{40}$/.test(raw)
    ? raw
    : 'dev-unset';
  return {
    buildId,
    containment: RELEASE_CONTAINMENT,
    keepsakeEnvelope: RELEASE_KEEPSAKE_ENVELOPE,
  };
}

// Frozen module-scope tuple: the single in-memory instance both the
// window global and Settings About expose.
const tuple: AdiaReleaseCapabilities = Object.freeze(buildTuple());

/** Install `window.__ADIA_RELEASE_CAPABILITIES__`. Called from main.tsx. */
export function installReleaseCapabilities(): void {
  if (typeof window === 'undefined') return;
  window.__ADIA_RELEASE_CAPABILITIES__ = tuple;
}

/** The same in-memory tuple, for Settings About. */
export function getReleaseCapabilities(): AdiaReleaseCapabilities {
  return tuple;
}
